<script context="module" lang="ts">
  // A forecast is worked out by the optimizer, which is worth not repeating each time the athlete
  // comes back to this tab: keep the last result for as long as the plan, the calendar settings and the
  // medians it was made from are all the same objects.
  const cache = new WeakMap<object, { calendarSettings: unknown; medianValues: unknown; data: unknown[] }>();
</script>

<script lang="ts">
  import { getContext } from 'svelte';
  import { readable } from 'svelte/store';
  import { Button } from '@/lib/components/ui/button';
  import { Skeleton } from '@/lib/components/ui/skeleton';
  import Am5Loader from '@/Util/Am5Loader.svelte';
  import { getForecastResultsForChart } from '@/Util/utils';
  import type { MyFitnessStores } from '../stores';
  import ForecastChart from '../TrainingForecast/ProgressionChart.svelte';

  // The athlete's current training plan and what it forecasts, exactly as the Program tab charts them.
  const myFitness = getContext<MyFitnessStores | undefined>('myFitness');
  const medians = getContext<{ trainingMedians: any } | undefined>('medianAndWeather');
  const trainingPlan = myFitness?.trainingPlan ?? readable<any>(null);
  const calSettings = myFitness?.cal_settings ?? readable<any>(null);
  const userParams = myFitness?.userParams ?? readable<any>({});
  const trainingMedians = medians?.trainingMedians ?? readable<any>(null);

  // The same height as the other charts on this tab (a little shorter on a phone).
  const height = window.matchMedia('(min-width: 768px)').matches ? '360px' : '300px';

  let state: 'loading' | 'empty' | 'error' | 'ready' = 'loading';
  let chartData: unknown[] = [];
  let latest = 0;

  $: update($trainingPlan, $calSettings, $trainingMedians);

  async function update(plan: any, calendarSettings: any, medianValues: any) {
    const request = ++latest;
    if (!plan?.forecastResults?.days?.length) {
      state = 'empty';
      return;
    }
    const cached = cache.get(plan);
    if (cached && cached.calendarSettings === calendarSettings && cached.medianValues === medianValues) {
      chartData = cached.data;
      state = 'ready';
      return;
    }
    // Needs the calendar settings to have arrived: they set what days the athlete can train.
    if (!calendarSettings) return;
    state = 'loading';
    try {
      const result = await getForecastResultsForChart(plan.forecastResults, plan.settings, calendarSettings, medianValues);
      if (request !== latest) return;
      chartData = result.forecastResults['days'];
      cache.set(plan, { calendarSettings, medianValues, data: chartData });
      state = 'ready';
    } catch (error) {
      if (request !== latest) return;
      state = 'error';
    }
  }

  // The tab bar lives in the page above; it listens for this.
  function openProgram(event: MouseEvent) {
    (event.currentTarget as HTMLElement).dispatchEvent(
      new CustomEvent('myfitnesstabchange', { bubbles: true, detail: { tabName: 'program' } })
    );
  }
</script>

{#if state === 'ready'}
  <div class="flex flex-col gap-2">
    <!-- The scales, named in their own colours like the other charts. Inner scale first. -->
    <div class="flex flex-row justify-end gap-2 text-[10px] md:text-xs font-semibold leading-none" aria-hidden="true">
      <span style="color: #B8860B">FP</span>
      <span style="color: #1F2937">TL</span>
    </div>
    <Am5Loader>
      <ForecastChart id="progression-forecast-chart" athletes={$userParams?.athletes} data={chartData} range={null} compact {height} />
    </Am5Loader>
  </div>
{:else if state === 'loading'}
  <div class="flex flex-col gap-2 w-full">
    <Skeleton class="h-[25px] w-[40%]" />
    <Skeleton class="h-[360px] w-full" />
  </div>
{:else if state === 'error'}
  <div class="flex h-[200px] items-center justify-center text-sm text-red-600">Couldn't work out your forecast.</div>
{:else}
  <div class="flex h-[240px] flex-col items-center justify-center gap-3 text-center text-sm text-muted-foreground">
    <div>Your forecast comes from a training program, and you don't have one set up yet.</div>
    <Button variant="outline" size="sm" on:click={openProgram}>Set up a program</Button>
  </div>
{/if}
